<template>
  <section class="projects">
    <div class="background-animation"></div>
    <h2 ref="sectionTitle">{{ $t('projects.title') }}</h2>
    <div class="projects-container">
      <div v-for="(project, index) in projects" :key="project.id" class="project-card" :ref="el => projectCards[index] = el">
        <div class="project-status" v-if="project.status">
          <span>{{ $t(`projects.projectsList[${index}].status`) }}</span>
        </div>
        <div class="project-image">
          <img :class="project.customClass" :src="project.image" :alt="$t(`projects.projectsList[${index}].title`)" />
        </div>
        <div class="project-details">
          <div class="project-content">
            <h3>{{ $t(`projects.projectsList[${index}].title`) }}</h3>
            <p>{{ $t(`projects.projectsList[${index}].description`) }}</p>
          </div>
          <div class="tech-stack" v-if="project.techStack.length > 0">
            <h4>{{ $t('projects.techStack') }}</h4>
            <ul class="tech-list">
              <li v-for="tech in project.techStack" :key="tech.name">
                <img :src="tech.icon" :alt="tech.name" class="tech-icon" /> {{ tech.name }}
              </li>
            </ul>
          </div>
          <a v-if="project.link" :href="project.link" target="_blank" class="project-link">{{ $t('projects.viewProject') }}</a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const projects = reactive([
  {
    id: 1,
    image: "AiBot.png", 
    techStack: [
      { name: "Vue.js", icon: "vue.svg" },
      { name: "OpenAI", icon: "openai.svg" },
      { name: "Flask", icon: "flask.png" },
      { name: "Python", icon: "python.svg" },
      { name: "ChromaDB", icon: "chromadb.webp" }
    ],
  },
  {
    id: 2,
    image: "diag.jpg",
    techStack: [
      { name: "Python", icon: "python.svg" },
      { name: "OpenAI", icon: "openai.svg" },
    ],
  },
  {
    id: 3,
    customClass: "bg-white",
    image: "rolm.webp",
    techStack: [
      { name: "Shopware 6", icon: "shopware.svg" },
      { name: "Vue.js", icon: "vue.svg" },
      { name: "Twig", icon: "twig.png" },
      { name: "JavaScript", icon: "js.png" },
      { name: "Sass", icon: "scss.png" }
    ],
  },
  {
    id: 4,
    image: "epsylon.png",
    link: "https://br-epsylon.pl",
    techStack: [
      { name: "Next.js", icon: "next.svg" },
      { name: "MUI", icon: "mui.png" },
      { name: "CSS", icon: "css.svg" }
    ],
  },
  {
    id: 5,
    image: "sv.webp",
    techStack: [
      { name: "Next.js", icon: "next.svg" },
      { name: "MUI", icon: "mui.png" },
      { name: "TypeScript", icon: "ts.png" }
    ],
  },
  {
    id: 6,
    customClass: "bg-white",
    image: "kombuch.webp",
    techStack: [
      { name: "WordPress", icon: "wordpress.svg" },
      { name: "WooCommerce", icon: "woo.svg" },
    ],
  },
  {
    id: 7,
    image: "flatscanner.png",
    techStack: [
      { name: "Nuxt.js", icon: "nuxt.svg" },
      { name: "Vuex", icon: "vuex.png" },
      { name: "Vuetify", icon: "vuetify.svg" }
    ],
  },
]);

const sectionTitle = ref(null);
const projectCards = ref([]);

onMounted(() => {
  // Animate section title
  gsap.from(sectionTitle.value, {
    scrollTrigger: {
      trigger: sectionTitle.value,
      start: "top bottom-=100",
      toggleActions: "play none none reverse"
    },
    opacity: 0,
    y: 50,
    duration: 1,
    ease: "power3.out"
  });

  // Animate project cards
  projectCards.value.forEach((card, index) => {
    gsap.from(card, {
      scrollTrigger: {
        trigger: card,
        start: "top bottom-=50",
        toggleActions: "play none none reverse"
      },
      opacity: 0,
      y: 100,
      rotation: 5,
      scale: 0.9,
      duration: 0.8,
      ease: "back.out(1.7)",
      delay: index * 0.1
    });
  });

  // Animate tech stack icons
  gsap.utils.toArray('.tech-icon').forEach((icon) => {
    gsap.from(icon, {
      scrollTrigger: {
        trigger: icon,
        start: "top bottom-=20",
        toggleActions: "play none none reverse"
      },
      opacity: 0,
      scale: 0,
      rotation: 360,
      duration: 0.5,
      ease: "back.out(2)"
    });
  });

  // Animate background
  gsap.to('.background-animation', {
    backgroundPosition: '100% 100%',
    duration: 20,
    repeat: -1,
    ease: 'none'
  });

  // Create floating particles
  const particlesContainer = document.createElement('div');
  particlesContainer.className = 'particles-container';
  document.querySelector('.projects').appendChild(particlesContainer);

  for (let i = 0; i < 50; i++) {
    const particle = document.createElement('div');
    particle.className = 'particle';
    particlesContainer.appendChild(particle);

    gsap.set(particle, {
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      scale: Math.random() * 0.5 + 0.5,
    });

    gsap.to(particle, {
      x: '+=' + (Math.random() * 100 - 50),
      y: '+=' + (Math.random() * 100 - 50),
      opacity: Math.random() * 0.5 + 0.5,
      duration: Math.random() * 4 + 4,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });
  }
});
</script>

<style scoped>
.projects {
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 20px;
  background-color: #121212;
  color: white;
  position: relative;
  overflow: hidden;
}

.background-animation {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0a0a0a;
  z-index: 1;
}

.particles-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.particle {
  position: absolute;
  width: 5px;
  height: 5px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.projects h2 {
  position: relative;
  z-index: 2;
  margin-bottom: 2rem;
  font-size: 3rem;
  font-weight: 700;
  color: #fff;
}

.projects-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  width: 100%;
  position: relative;
  z-index: 2;
}

.project-card {
  background: rgba(34, 34, 34, 0.8);
  padding: 20px;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
}

.project-card:hover {
  transform: scale(1.05) translateY(-10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.project-image {
  width: 100%;
  aspect-ratio: 4 / 3;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.project-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
  transition: transform 0.5s ease;
}

.project-card:hover .project-image img {
  transform: scale(1.1);
}

.project-status {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background-color: #ff4081;
  color: white;
  padding: 5px 25px 5px 10px;
  font-size: 0.9rem;
  font-weight: bold;
  clip-path: polygon(0 0, 100% 0, 70% 100%, 0% 100%);
}

.project-details {
  padding: 10px;
  z-index: 2;
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.project-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.project-content h3 {
  margin-bottom: 0.5rem;
}

.project-content p {
  flex-grow: 1;
}

.project-link {
  color: #00bcd4;
  text-decoration: none;
  font-weight: bold;
  display: block;
  margin-top: 10px;
  transition: color 0.3s ease;
  position: relative;
  overflow: hidden;
}

.project-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #00bcd4;
  transform: scaleX(0);
  transform-origin: right;
  transition: transform 0.3s ease;
}

.project-link:hover {
  color: #fff;
}

.project-link:hover::after {
  transform: scaleX(1);
  transform-origin: left;
}

.tech-stack {
  margin-top: auto;
}

.tech-stack h4 {
  margin-top: 10px;
  font-size: 1.1rem;
  font-weight: 700;
  color: #00bcd4;
}

.tech-list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.tech-list li {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #fff;
}

.tech-icon {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
}

@media (max-width: 1200px) {
  .projects-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .projects-container {
    grid-template-columns: 1fr;
  }
}
</style>